import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { datadogRum } from '@datadog/browser-rum';
import { AXNUIAsyncWaitingPage } from "@iddataweb/idw-common-ui/containers";
import { AxnError, getRedirectUrlWithoutHostname } from "@iddataweb/idw-common-ui/utils";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { getAsyncModalMessage, getAsyncTimeoutID } from "@redux/selectors/verification-form-selectors";
import { getContextPath, getQueryParametersApSessionId } from "@redux/selectors/query-parameters-selectors";
import { setError } from "@redux/actions/error-actions";
import { getAsi, getAsyncInterval, getOidcCallbackURL, getRedirectToCallbackUrlOnError } from "@redux/selectors/verification-configuration-selectors";
import { setLandingPageData, setRedirectUrl } from "@redux/actions/common-actions";
import { setAsyncTimeoutId } from "@redux/actions/verification-form-actions";
import { pollForAsyncStatus } from "@requests/poll-for-async-status";
import { Logger } from '@utils/logger';
import { handleNavigationForError, navigateToUtilWithQueryParameters } from "@utils/navigation-util";
import { LoadingSpinner } from "../common/loading-spinner/loading-spinner";

export const WaitPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const content = useSelector(getLanguageContent);
    const asi = useSelector(getAsi);
    const apSessionId = useSelector(getQueryParametersApSessionId);
    const asyncModalMessage = useSelector(getAsyncModalMessage);
    const contextPath = useSelector(getContextPath);
    const asyncTimeoutID = useSelector(getAsyncTimeoutID);
    const asyncInterval = useSelector(getAsyncInterval);
    const asyncPollErrorCount = useRef(0);
    const redirectToCallbackUrlOnError = useSelector(getRedirectToCallbackUrlOnError);
    const oidcCallbackURL = useSelector(getOidcCallbackURL);

    const [hasReceivedResponse, setHasReceivedResponse] = useState(false);

    useEffect(() => {
        performPollForAsyncStatus();
    }, []);

    const setupTimeoutToPollForAsyncSuccess = () => {
        if (asyncTimeoutID) {
            Logger.info(`Cleared async timeout ${asyncTimeoutID}`);
            clearTimeout(asyncTimeoutID);
        }
        const newAsyncTimeoutID = setTimeout(() => { performPollForAsyncStatus() }, asyncInterval);
        Logger.info(`Created new timeout ${newAsyncTimeoutID}`);
        dispatch(setAsyncTimeoutId(newAsyncTimeoutID));
    };

    const handleErrorWithFaultTolerance = (error: Error) => {
        Logger.error(error);
        datadogRum.addError(error);
        asyncPollErrorCount.current++;
        setupTimeoutToPollForAsyncSuccess();
    };

    const performPollForAsyncStatus = async () => {
        try {
            const response: Response = await pollForAsyncStatus(apSessionId, asi, contextPath);

            if (response.ok) {
                const data = await response.json();

                if (data.status === 'fail') {
                    const error = {
                        name: "Poll for Async Status Responded with Fail",
                        asi: apSessionId,
                        errorCode: data.errorCode || `${response.status}`,
                        message: data.errorMessage || `${response.statusText}`,
                        errorURI: response.url,
                        httpStatus: `${response.status}`,
                        forwardedErrorCode: data.errorCode,
                        forwardedErrorDescription: data.errorMessage
                    };
                    throw error;
                } else if (data.status === 'pending') {
                    setHasReceivedResponse(true);
                    // Not ready yet
                    Logger.info('Async request incomplete. Try again');
                    setupTimeoutToPollForAsyncSuccess();
                } else if (data.status === 'complete') {
                    Logger.info('Async request complete. Handling response');

                    if (data.idToken && data.resultsDisplayType && data.url) {
                        Logger.info(`New mockrp triggered - redirecting internally`);
                        const trimmedRedirectUrl = data.url.split("?")[0];
                        const redirectUrlWithoutHostname = getRedirectUrlWithoutHostname(trimmedRedirectUrl);
                        dispatch(setRedirectUrl(redirectUrlWithoutHostname));
                        dispatch(setLandingPageData(data.idToken));
                        navigateToUtilWithQueryParameters(`/${redirectUrlWithoutHostname}`, navigate, location.pathname, location.search);
                    } else if (data.url) {
                        window.location.href = data.url;
                    }
                }
            } else {
                const error: AxnError = {
                    name: "Poll for Async Status Error",
                    asi: apSessionId,
                    errorCode: `${response.status}`,
                    message: response.statusText,
                    errorURI: response.url,
                    httpStatus: `${response.status}`
                };
                throw error;
            }
        } catch (error) {
            if (asyncPollErrorCount.current > 0) {
                Logger.error(error);
                datadogRum.addError(error);
                dispatch(setError(error));
                handleNavigationForError(dispatch)(
                    redirectToCallbackUrlOnError,
                    oidcCallbackURL,
                    asi,
                    "/error",
                    navigate,
                    location.pathname,
                    location.search,
                    error
                );
            } else {
                setHasReceivedResponse(true);
                handleErrorWithFaultTolerance(error);
            }
        }
    };

    return hasReceivedResponse ? (
        <AXNUIAsyncWaitingPage
            axnuiContent={content}
            asyncModalMessage={asyncModalMessage}
        />
    ) : (
        <LoadingSpinner loadingText="" />
    )
};